import { Dispatch } from "react";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { BAND_ACTIONS, IEventVenueBlockParams } from "Bands/types";
import { EventVenueBlockService } from "Bands/services";
import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";

export const dispatchGetEventVenueBlocks = (dispatch: Dispatch<any>, event?: string) => {
  dispatch(
    getEventVenueBlocks({
      event,
      page: 1, // we assume all blocks will be returned in page 1
      itemsPerPage: 100, // avoid pagination
    })
  );
};

export const getEventVenueBlocks = createAsyncThunk(
  BAND_ACTIONS.getEventVenueBlocks,
  async (
    {
      event,
      itemsPerPage,
      page,
    }: {
      event?: string;
      page: number;
      itemsPerPage: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventVenueBlockService.getBlocks({
        event: event ? API_ENDPOINTS.EVENT(event) : undefined,
        page,
        itemsPerPage,
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.eventVenueBlock]: data.eventVenueBlock,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetEventVenueBlock = (dispatch: Dispatch<any>, blockId: string) => {
  const action = API_ENDPOINTS.EVENT_VENUE_BLOCK(blockId);
  return dispatch(getEventVenueBlock({ blockId, action }));
};

export const getEventVenueBlock = createAsyncThunk(
  BAND_ACTIONS.getEventVenueBlock,
  async ({ blockId }: { blockId: string; action: string }, { rejectWithValue }) => {
    try {
      const block = await EventVenueBlockService.getBlock(blockId);

      return block;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createEventVenueBlock = createAsyncThunk(
  BAND_ACTIONS.createEventVenueBlock,
  async (block: IEventVenueBlockParams, { rejectWithValue }) => {
    try {
      const data = await EventVenueBlockService.createBlock(block);
      const id = Object.values(data?.eventVenueBlock)?.[0]?.attributes?.id;
      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventVenueBlock = createAsyncThunk(
  BAND_ACTIONS.editEventVenueBlock,
  async (props: { id: string; data: IEventVenueBlockParams }, { rejectWithValue }) => {
    try {
      const data = await EventVenueBlockService.editBlock(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEventVenueBlock = createAsyncThunk(
  BAND_ACTIONS.deleteEventVenueBlock,
  async (id: string, { rejectWithValue }) => {
    try {
      await EventVenueBlockService.deleteBlock(id);
      return {
        path: API_ENDPOINTS.EVENT_VENUE_BLOCK(id),
        dataKey: IDataKeys.eventVenueBlock,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
